import React, { useState } from 'react';

const COLUMNS = [
  { id: 'Saved',        label: 'Saved',        color: '#64748b' },
  { id: 'Applied',      label: 'Applied',      color: '#3b82f6' },
  { id: 'Screen',       label: 'Screen',       color: '#06b6d4' },
  { id: 'Interviewing', label: 'Interviewing', color: '#f59e0b' },
  { id: 'Offer',        label: 'Offer',        color: '#10b981' },
  { id: 'Accepted',     label: 'Accepted',     color: '#059669' },
  { id: 'Rejected',     label: 'Rejected',     color: '#ef4444' },
];

function Stars({ value, onChange }) {
  return (
    <div className="stars" onClick={e => e.stopPropagation()}>
      {[1,2,3,4,5].map(n => (
        <span
          key={n}
          className={`star ${n <= value ? 'on' : ''}`}
          onClick={() => onChange(n === value ? 0 : n)}
        >
          {n <= value ? '★' : '☆'}
        </span>
      ))}
    </div>
  );
}

function remoteTag(type) {
  if (!type) return null;
  const cls = type === 'Remote' ? 'remote' : type === 'Hybrid' ? 'hybrid' : 'onsite';
  return <span className={`tag ${cls}`}>{type}</span>;
}

function jobTypeTag(type) {
  if (!type || type === 'Full-time') return null;
  const cls = type === 'Internship' ? 'internship' : type === 'Contract' ? 'contract' : 'fulltime';
  return <span className={`tag ${cls}`}>{type}</span>;
}

function fmt(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function InlineField({ value, placeholder, onSave, bold = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');

  const start = (e) => {
    e.stopPropagation();
    setVal(value || '');
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    if (val !== (value || '')) onSave(val);
  };
  const onKey = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') { setVal(value || ''); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
        onClick={e => e.stopPropagation()}
        className="card-inline-input"
      />
    );
  }

  return (
    <span
      className={`card-inline-field${bold ? ' card-inline-bold' : ''}`}
      onClick={start}
      title="Click to edit"
    >
      {value || <span className="card-inline-empty">{placeholder}</span>}
    </span>
  );
}

function ApplicationCard({ app, onEdit, onDelete, onArchive, onStatusChange, onRatingChange, onFieldChange, onCardClick, selected, linkedContacts = [] }) {
  const keywords = Array.isArray(app.keywords) ? app.keywords.slice(0, 4) : [];

  return (
    <div
      className={`app-card ${selected ? 'selected' : ''}`}
      onClick={() => onCardClick(app)}
    >
      <div className="card-top">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="card-company">
            <InlineField
              value={app.company}
              placeholder="Company"
              bold
              onSave={val => onFieldChange(app.id, { company: val })}
            />
          </div>
          <div className="card-role">
            <InlineField
              value={app.role}
              placeholder="Role"
              onSave={val => onFieldChange(app.id, { role: val })}
            />
          </div>
        </div>
        <div className="card-actions">
          <button className="btn-icon" onClick={e => { e.stopPropagation(); onEdit(app); }} title="Edit">✏️</button>
          {onArchive && <button className="btn-icon" onClick={e => { e.stopPropagation(); onArchive(app.id); }} title="Archive">📦</button>}
          <button className="btn-icon" onClick={e => { e.stopPropagation(); onDelete(app.id); }} title="Delete">🗑️</button>
        </div>
      </div>

      <div className="card-tags">
        {remoteTag(app.remote_type)}
        {jobTypeTag(app.job_type)}
      </div>

      {(app.location !== undefined) && (
        <div className="card-detail">
          📍 <InlineField
            value={app.location}
            placeholder="Add location"
            onSave={val => onFieldChange(app.id, { location: val })}
          />
        </div>
      )}
      {(app.salary !== undefined) && (
        <div className="card-detail">
          💰 <InlineField
            value={app.salary}
            placeholder="Add salary"
            onSave={val => onFieldChange(app.id, { salary: val })}
          />
        </div>
      )}
      {app.applied_date && <div className="card-detail">📅 {fmt(app.applied_date)}</div>}
      {app.deadline && <div className="card-detail card-deadline">⏰ Deadline: {fmt(app.deadline)}</div>}
      {app.job_url && (
        <a href={app.job_url} target="_blank" rel="noreferrer" className="card-link" onClick={e => e.stopPropagation()}>
          View Job →
        </a>
      )}

      {keywords.length > 0 && (
        <div className="card-keywords">
          {keywords.map(k => <span key={k} className="keyword-chip">{k}</span>)}
          {app.keywords.length > 4 && <span className="keyword-chip">+{app.keywords.length - 4}</span>}
        </div>
      )}

      {linkedContacts.length > 0 && (
        <div className="card-linked-contact" onClick={e => e.stopPropagation()}>
          <div className="linked-contacts-stack">
            {linkedContacts.slice(0, 3).map(c => (
              <span key={c.id} className="card-linked-avatar stacked" title={c.name}>
                {(c.name || '?').charAt(0).toUpperCase()}
              </span>
            ))}
            {linkedContacts.length > 3 && <span className="card-linked-avatar stacked more">+{linkedContacts.length - 3}</span>}
          </div>
          <span className="card-linked-name">
            {linkedContacts.length === 1 ? linkedContacts[0].name : `${linkedContacts.length} contacts`}
          </span>
          {linkedContacts.length === 1 && linkedContacts[0].contact_status && linkedContacts[0].contact_status !== 'Not Contacted' && (
            <span className="card-linked-status">{linkedContacts[0].contact_status}</span>
          )}
        </div>
      )}
      <div className="card-footer">
        <Stars value={app.excitement_rating || 0} onChange={r => onRatingChange(app.id, r)} />
        <select
          className="status-select"
          value={app.status}
          onChange={e => { e.stopPropagation(); onStatusChange(app.id, e.target.value); }}
          onClick={e => e.stopPropagation()}
        >
          {['Saved','Applied','Screen','Interviewing','Offer','Withdrawn','Ghosted','Rejected','Accepted'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );
}

export default function KanbanBoard({ applications, contacts = [], onEdit, onDelete, onArchive, onStatusChange, onRatingChange, onFieldChange, onCardClick, selectedId }) {
  return (
    <div className="kanban-board">
      {COLUMNS.map(col => {
        const cards = applications.filter(a => a.status === col.id);
        return (
          <div key={col.id} className="kanban-column">
            <div className="column-header" style={{ borderTopColor: col.color }}>
              <h2 style={{ color: col.color }}>{col.label}</h2>
              <span className="column-count" style={{ background: col.color }}>{cards.length}</span>
            </div>
            <div className="column-body">
              {cards.length === 0
                ? <p className="empty-column">No applications</p>
                : cards.map(app => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    selected={selectedId === app.id}
                    linkedContacts={contacts.filter(c => String(c.application_id) === String(app.id))}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onArchive={onArchive}
                    onStatusChange={onStatusChange}
                    onRatingChange={onRatingChange}
                    onFieldChange={onFieldChange}
                    onCardClick={onCardClick}
                  />
                ))
              }
            </div>
          </div>
        );
      })}
    </div>
  );
}
